import axios from 'axios';
import store from '../store';
import {dispatchEvent} from '../hooks/dispatchEvent';
import {authRefreshToken} from '../redux/auth/authActions';
import {iframeClick} from '../functions/iframeClick';

// Create a completely separate axios instance for token refresh operations
// This instance will have NO interceptors to prevent infinite loops
export const noInterceptorsAxios = axios.create({
    withCredentials: true,
    baseURL: process.env.REACT_APP_API_URL
});

// Track if a refresh token operation is in progress
let refreshTokenInProgress = null;

// localStorage is shared across all tabs and always holds the latest exp/csrfToken
// after ANY tab refreshes the token; per-tab redux can be stale and cause a forced
// logout of a still-valid session (refresh in tab A, logout at old exp in tab B)
export const getStoredAuthValue = (key) => {
    const value = localStorage.getItem(key);
    if(value === null)
        return null;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

export const refreshTokenIfNeed = () => {
    // let exp = store.getState().auth.exp;
    let exp = getStoredAuthValue('exp') || store.getState().auth.exp;
    if(!exp)
        return;

    const now = new Date();
    // If token is expired, trigger logout
    if(now > new Date(exp * 1000)) {
        iframeClick('logout');
        return;
    }

    // If token will expire within 1 hour and no refresh is in progress
    if(now.getTime() / 1000 >= exp - 3600 && !refreshTokenInProgress) {
        // Set a promise to track the refresh operation
        refreshTokenInProgress = authRefreshToken()
            .catch(console.error)
            .finally(() => {
                // Clear the tracking promise when done
                refreshTokenInProgress = null;
            });
    }

    return refreshTokenInProgress;
};

const handleErrors = (err) => {
    if(err?.message === 'Network Error') {
        // TODO: handle
        return true;
    }
    const {response} = err;
    const isBaseURL = err.config.baseURL === axios.defaults.baseURL;
    if(!response || response.status >= 500) {
        // TODO: handle
        return true;
    }
    if(response.status === 401 && isBaseURL && !err.config.url.startsWith('/auth')) {
        dispatchEvent('401Exception');
        // TODO: redirect
        return true;
    }
    return false;
};

// Special utility function to make direct API calls without any interceptors
export const makeDirectApiCall = async (method, url, data = {}, config = {}) => {
    // const csrfToken = store.getState().auth.csrfToken;
    const csrfToken = getStoredAuthValue('csrfToken') || store.getState().auth.csrfToken;
    const headers = {'X-CSRF-Token': csrfToken, ...(config.headers || {})};

    return noInterceptorsAxios({
        method,
        url,
        data,
        headers,
        ...config
    });
};

export const initAxios = () => {
    axios.defaults.withCredentials = true;
    axios.defaults.baseURL = process.env.REACT_APP_API_URL;

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            error.isTreated = handleErrors(error);
            return Promise.reject(error);
        },
    );

    axios.interceptors.request.use((config) => {
        // Get globalUserId from the user object in localStorage
        const userString = localStorage.getItem('user');
        let globalUserId = null;
        if(userString) {
            try {
                const userData = JSON.parse(userString);
                globalUserId = userData?.globalUserId;
            } catch (e) {
                console.error('Error parsing user data from localStorage', e);
            }
        }

        if(globalUserId) {
            config.headers = {
                ...config.headers,
                'X-Local-User-ID': globalUserId
            };
        }
        if(config.url !== '/logout/' && config.url !== '/user/refreshToken/' && !config.skipTokenRefresh) {
            refreshTokenIfNeed();
        }
        return config;
    });
};
